/* FILE: ./frontend/src/components/NavBar.jsx */
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';

export default function NavBar() {
  const { user, userProfile, signOut, isSupplier } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [supportForm, setSupportForm] = useState({ email: '', message: '', type: 'bug' });
  const [supportStatus, setSupportStatus] = useState('idle');

  const isAdmin = userProfile?.role === 'admin';

  // ⚡ SEO FIX: Helper to determine if a route is active (ignoring the /fr prefix)
  const isActive = (path) => location.pathname === path || location.pathname === `/fr${path === '/' ? '' : path}`;

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
    setIsOpen(false);
    setIsProfileOpen(false);
  };

  const handleSupportClick = () => {
      setIsOpen(false);
      if (user) {
          navigate('/inbox?tab=support');
      } else {
          setIsSupportModalOpen(true);
      }
  };

  const submitPublicTicket = async (e) => {
      e.preventDefault();
      setSupportStatus('submitting');
      try {
          const { error } = await supabase.from('public_support_tickets').insert([{
              sender_email: supportForm.email,
              ticket_type: supportForm.type,
              message: supportForm.message,
              status: 'open'
          }]);
          if (error) throw error;
          setSupportStatus('success');
          setTimeout(() => {
              setIsSupportModalOpen(false);
              setSupportStatus('idle');
              setSupportForm({ email: '', message: '', type: 'bug' });
          }, 3000);
      } catch (err) {
          alert("Failed to send message: " + err.message);
          setSupportStatus('idle');
      }
  };

  const toggleMenu = () => setIsOpen(!isOpen);

  // ⚡ SEO FIX: Toggle button now physically updates the URL to force re-render under proper language route
  const toggleLanguage = () => {
    const currentPath = location.pathname;
    if (currentPath.startsWith('/fr')) {
        // Strip out /fr and navigate to English
        const newPath = currentPath.replace(/^\/fr/, '') || '/';
        navigate(newPath);
    } else {
        // Add /fr and navigate to French
        const newPath = `/fr${currentPath === '/' ? '' : currentPath}`;
        navigate(newPath);
    }
  };

  return (
    <>
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm print:hidden">
      <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          
          <div className="flex items-center shrink-0 pr-4">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-xs">TB</span>
              </div>
              <span className="text-xl md:text-2xl font-black text-blue-600 tracking-tight transition-colors group-hover:text-blue-700 truncate">
                TariffBeater
              </span>
            </Link>
          </div>

          <div className="hidden xl:flex items-center justify-center gap-4 xl:gap-8 flex-grow">
            <Link to="/directory" className={`font-medium text-[15px] whitespace-nowrap transition-colors ${isActive('/directory') ? 'text-blue-600 font-bold' : 'text-gray-700 hover:text-blue-600'}`}>
                {t('nav.directory')}
            </Link>
            <Link to="/simulator" className={`font-medium text-[15px] whitespace-nowrap transition-colors ${isActive('/simulator') ? 'text-blue-600 font-bold' : 'text-gray-700 hover:text-blue-600'}`}>
                {t('nav.simulator')}
            </Link>
            
            {isAdmin && (
              <Link to="/admin" className="text-red-600 hover:text-red-800 font-bold text-base flex items-center border border-red-200 bg-red-50 px-3 py-1 rounded whitespace-nowrap shrink-0">
                  👮 {t('nav.admin')}
              </Link>
            )}

            {user && (
               <Link to="/inbox" className={`font-medium text-[15px] flex items-center whitespace-nowrap transition-colors ${isActive('/inbox') ? 'text-blue-600 font-bold' : 'text-gray-700 hover:text-blue-600'}`}>
                 {t('nav.inbox')}
               </Link>
            )}

            {user && isSupplier && (
                <div className="flex gap-4 xl:gap-6 border-l border-gray-200 pl-4 xl:pl-6 shrink-0">
                  <Link to="/inventory" className={`font-medium text-[15px] flex items-center whitespace-nowrap transition-colors ${isActive('/inventory') ? 'text-blue-600 font-bold' : 'text-gray-700 hover:text-blue-600'}`}>
                    📦 {t('nav.inventory')}
                  </Link>
                  <Link to="/analytics" className={`font-medium text-[15px] flex items-center whitespace-nowrap transition-colors ${isActive('/analytics') ? 'text-blue-600 font-bold' : 'text-gray-700 hover:text-blue-600'}`}>
                    📊 {t('nav.analytics')}
                  </Link>
                </div>
            )}
          </div>

          <div className="hidden xl:flex items-center gap-3 shrink-0 pl-4">
            
            <button 
                onClick={toggleLanguage}
                className="text-xs font-black uppercase tracking-widest text-slate-500 hover:text-blue-600 border border-slate-200 hover:border-blue-200 px-2 py-1 rounded transition-colors shrink-0"
                title="Toggle Language"
            >
                {i18n.language.startsWith('en') ? 'FR' : 'EN'}
            </button>

            <button 
                onClick={handleSupportClick}
                className="text-gray-500 hover:text-blue-600 font-bold text-sm px-3 py-2 rounded-lg transition-colors flex items-center gap-1 shrink-0 whitespace-nowrap"
            >
                <span>🎧</span> {t('nav.support')}
            </button>

            {user ? (
              <>
                <Link 
                    to="/billing"
                    className="text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-2 rounded-lg font-bold text-sm transition-colors flex items-center gap-1 shrink-0 whitespace-nowrap"
                >
                    <span>💎</span> {t('nav.upgrade')}
                </Link>

                {isSupplier ? (
                  <Link 
                    to="/supplier-onboarding" 
                    className="bg-slate-800 hover:bg-black text-white px-4 py-2 rounded-lg font-bold text-sm shadow-md transition-all flex items-center border border-slate-700 whitespace-nowrap shrink-0"
                  >
                    <span>🏢 {t('nav.my_company')}</span>
                  </Link>
                ) : (
                  <Link 
                    to="/supplier-onboarding" 
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold text-sm shadow-md transition-all flex items-center whitespace-nowrap shrink-0"
                  >
                    <span>🏭 {t('nav.list_business')}</span>
                  </Link>
                )}

                <div className="relative ml-1 shrink-0">
                  <button 
                      onClick={() => setIsProfileOpen(!isProfileOpen)}
                      className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 text-blue-700 font-bold border-2 border-blue-200 hover:bg-blue-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shrink-0"
                      title={user.email || 'User'}
                  >
                      {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                  </button>

                  {isProfileOpen && (
                      <>
                          <div className="fixed inset-0 z-40" onClick={() => setIsProfileOpen(false)}></div>
                          <div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                                  <p className="text-sm text-gray-900 font-bold truncate">{user.email || 'Signed In'}</p>
                              </div>
                              <div className="py-2">
                                  <Link to="/account" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                                      ⚙️ {t('nav.account')}
                                  </Link>
                              </div>
                              <div className="border-t border-gray-100 py-1">
                                  <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 transition-colors">
                                      {t('nav.logout')}
                                  </button>
                              </div>
                          </div>
                      </>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="text-gray-600 hover:text-gray-900 font-medium transition-colors whitespace-nowrap px-3 shrink-0">{t('nav.login')}</Link>
                <Link to="/register" className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-medium shadow-sm transition-all whitespace-nowrap shrink-0">
                  {t('nav.get_started')}
                </Link>
              </>
            )}
          </div>

          <div className="xl:hidden flex items-center gap-4">
             <button 
                onClick={toggleLanguage}
                className="text-xs font-black uppercase tracking-widest text-slate-500 border border-slate-200 px-2 py-1 rounded shrink-0"
            >
                {i18n.language.startsWith('en') ? 'FR' : 'EN'}
            </button>

            <button onClick={() => setIsOpen(!isOpen)} className="p-2 text-gray-500 hover:text-blue-600 focus:outline-none shrink-0">
               <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
               </svg>
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="xl:hidden border-t border-gray-100 bg-white py-2 space-y-1 pb-4 shadow-lg absolute w-full left-0 z-40 animate-fade-in-up print:hidden">
          <div className="px-4 pt-2 pb-6 space-y-2 shadow-inner">
            <Link to="/directory" onClick={toggleMenu} className={`block px-6 py-3 text-gray-700 hover:bg-gray-50 font-medium ${isActive('/directory') ? 'text-blue-600 bg-blue-50 font-bold' : ''}`}>{t('nav.directory')}</Link>
            <Link to="/simulator" onClick={toggleMenu} className={`block px-6 py-3 text-gray-700 hover:bg-gray-50 font-medium ${isActive('/simulator') ? 'text-blue-600 bg-blue-50 font-bold' : ''}`}>{t('nav.simulator')}</Link>
            
            {isAdmin && (
               <Link to="/admin" onClick={toggleMenu} className="block px-6 py-3 text-red-600 font-bold">
                 👮 {t('nav.admin')}
               </Link>
            )}

            {user && (
               <>
                 <Link to="/inbox" onClick={toggleMenu} className={`block px-6 py-3 text-gray-700 hover:bg-gray-50 font-medium ${isActive('/inbox') ? 'text-blue-600 bg-blue-50 font-bold' : ''}`}>{t('nav.inbox')}</Link>
                 <Link to="/billing" onClick={toggleMenu} className="block px-6 py-3 text-emerald-700 bg-emerald-50 font-bold">💎 {t('nav.upgrade')}</Link>
               </>
            )}

            {user && isSupplier && (
              <>
                <Link to="/inventory" onClick={toggleMenu} className="block px-6 py-3 text-gray-700 hover:bg-gray-50 font-medium">📦 {t('nav.inventory')}</Link>
                <Link to="/analytics" onClick={toggleMenu} className="block px-6 py-3 text-gray-700 hover:bg-gray-50 font-medium">📊 {t('nav.analytics')}</Link>
              </>
            )}
            
            <div className="border-t border-gray-100 my-2 mx-4"></div>
            
            <button onClick={handleSupportClick} className="w-full text-left block px-6 py-3 text-blue-600 hover:bg-blue-50 font-bold">🎧 {t('nav.support')}</button>
            
            {user ? (
                <>
                    <Link to="/supplier-onboarding" onClick={toggleMenu} className="block px-6 py-3 text-blue-600 hover:bg-blue-50 font-bold">
                        {isSupplier ? `🏢 ${t('nav.my_company')}` : `🏭 ${t('nav.list_business')}`}
                    </Link>
                    <Link to="/account" onClick={toggleMenu} className="block px-6 py-3 text-gray-700 hover:bg-gray-50 font-medium">⚙️ {t('nav.account')}</Link>
                    <button onClick={handleLogout} className="w-full text-left block px-6 py-3 text-red-600 hover:bg-red-50 font-medium">{t('nav.logout')}</button>
                </>
            ) : (
                <>
                    <Link to="/login" onClick={toggleMenu} className="block px-6 py-3 text-gray-700 hover:bg-gray-50 font-medium">{t('nav.login')}</Link>
                    <Link to="/register" onClick={toggleMenu} className="block px-6 py-3 text-blue-700 bg-blue-50 font-bold text-center border border-blue-100 rounded-lg mx-4 mt-2">{t('nav.get_started')}</Link>
                </>
            )}
          </div>
        </div>
      )}
    </nav>

    {isSupportModalOpen && !user && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-[100] backdrop-blur-sm animate-fade-in print:hidden">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                <div className="px-6 py-4 bg-blue-600 text-white flex justify-between items-center">
                    <h3 className="font-bold text-lg flex items-center gap-2"><span>🎧</span> {t('nav.support_modal_title')}</h3>
                    <button onClick={() => setIsSupportModalOpen(false)} className="text-blue-200 hover:text-white text-2xl">×</button>
                </div>
                
                {supportStatus === 'success' ? (
                    <div className="p-12 text-center">
                        <div className="text-5xl mb-4">✅</div>
                        <h4 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h4>
                        <p className="text-gray-500">Thanks for reaching out. Our team will review this shortly.</p>
                    </div>
                ) : (
                    <form onSubmit={submitPublicTicket} className="p-6 space-y-4">
                        <p className="text-sm text-gray-600 mb-4">{t('nav.support_modal_desc')}</p>
                        
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Your Email</label>
                            <input 
                                type="email" required
                                value={supportForm.email}
                                onChange={e => setSupportForm({...supportForm, email: e.target.value})}
                                className="w-full border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                                placeholder="you@company.com"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Topic</label>
                            <select 
                                value={supportForm.type}
                                onChange={e => setSupportForm({...supportForm, type: e.target.value})}
                                className="w-full border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                            >
                                <option value="bug">Report a Bug / Error</option>
                                <option value="login">Login / Registration Issue</option>
                                <option value="suggestion">Feature Suggestion</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Message</label>
                            <textarea 
                                required rows="4"
                                value={supportForm.message}
                                onChange={e => setSupportForm({...supportForm, message: e.target.value})}
                                className="w-full border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 focus:border-blue-500 resize-none"
                                placeholder="Please describe the issue..."
                            />
                        </div>
                        <button 
                            type="submit" 
                            disabled={supportStatus === 'submitting'}
                            className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 shadow-md transition-colors disabled:opacity-50 mt-2"
                        >
                            {supportStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    )}
    </>
  );
}